// src/components/tasks/ListsCard.tsx
// Dashboard card showing task lists with icons and quick access
import React, { useMemo } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { add, chevronForward, pinOutline } from 'ionicons/icons';
import { Card } from '../ui/Card';
import { IconDisplay } from '../ui/IconPicker';
import type { TaskList, ListType } from '../../types';
import './tasks.css';

export interface ListsCardProps {
  lists: TaskList[];
  loading?: boolean;
  onListClick: (list: TaskList) => void;
  onCreateList: () => void;
  onViewAll: () => void;
  maxItems?: number;
}

// List type configuration
const LIST_TYPE_CONFIG: Record<ListType, { label: string; icon: string; color: string }> = {
  todo: { label: 'To-Do', icon: '✅', color: 'var(--ion-color-primary)' },
  shopping: { label: 'Shopping', icon: '🛒', color: 'var(--ion-color-success)' },
  project: { label: 'Project', icon: '📋', color: 'var(--ion-color-tertiary)' },
  checklist: { label: 'Checklist', icon: '📝', color: 'var(--ion-color-warning)' },
};

export const ListsCard: React.FC<ListsCardProps> = ({
  lists,
  loading = false,
  onListClick,
  onCreateList,
  onViewAll,
  maxItems = 6,
}) => {
  // Separate pinned and regular lists, prioritize pinned
  const sortedLists = useMemo(() => {
    const active = lists.filter(l => !l.is_archived);
    // Sort: pinned first, then by sort_order
    return active.sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  }, [lists]);

  const displayedLists = sortedLists.slice(0, maxItems);
  const remainingCount = sortedLists.length - displayedLists.length;

  // Count stats
  const totalItems = lists.reduce((sum, l) => sum + (l.item_count || 0), 0);

  return (
    <Card
      title="Lists"
      subtitle={lists.length > 0 ? `${totalItems} items` : undefined}
      size="md"
    >
      <div className="lists-card">
        {/* Header with add button */}
        <div className="lists-card__header">
          <button
            className="lists-card__add-btn"
            onClick={onCreateList}
            aria-label="Create new list"
          >
            <IonIcon icon={add} />
          </button>
        </div>

        {loading ? (
          <div className="lists-card__loading">
            <IonSpinner name="crescent" />
          </div>
        ) : lists.length === 0 ? (
          <div className="lists-card__empty">
            <span className="lists-card__empty-icon">📋</span>
            <p>No lists yet</p>
            <button className="lists-card__create-btn" onClick={onCreateList}>
              <IonIcon icon={add} />
              Create a List
            </button>
          </div>
        ) : (
          <>
            {/* Lists Grid */}
            <div className="lists-card__grid">
              {displayedLists.map(list => {
                const typeConfig = LIST_TYPE_CONFIG[list.list_type] || LIST_TYPE_CONFIG.todo;

                return (
                  <button
                    key={list.id}
                    className="lists-card__item"
                    onClick={() => onListClick(list)}
                    style={{ '--list-color': list.color || typeConfig.color } as React.CSSProperties}
                  >
                    <div className="lists-card__item-icon">
                      {list.icon_type && list.icon_type !== 'emoji' && list.icon_url ? (
                        <IconDisplay
                          icon={{ type: list.icon_type, name: list.icon || '', imageUrl: list.icon_url }}
                          size={24}
                        />
                      ) : (
                        list.icon || typeConfig.icon
                      )}
                    </div>
                    <div className="lists-card__item-info">
                      <span className="lists-card__item-name">{list.name}</span>
                      <span className="lists-card__item-meta">
                        {list.item_count || 0} items
                        {list.is_pinned && (
                          <IonIcon icon={pinOutline} className="lists-card__pin-icon" />
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* View All button */}
            {remainingCount > 0 && (
              <button className="lists-card__view-all" onClick={onViewAll}>
                +{remainingCount} more lists
                <IonIcon icon={chevronForward} />
              </button>
            )}

            {/* Quick Create Buttons */}
            <div className="lists-card__quick-create">
              {(Object.keys(LIST_TYPE_CONFIG) as ListType[]).map(type => {
                const config = LIST_TYPE_CONFIG[type];
                return (
                  <button
                    key={type}
                    className="lists-card__type-btn"
                    onClick={() => {
                      // Navigate to create with type
                      window.location.href = `/lists/new?type=${type}`;
                    }}
                    title={`New ${config.label} List`}
                  >
                    <span>{config.icon}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

ListsCard.displayName = 'ListsCard';

export default ListsCard;
